import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import './ResetPassword.css'; // We'll create this CSS file

const STRONG_PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/;

/**
 * Completely rewritten ResetPassword component with simplified styling
 * - Uses plain CSS instead of styled-components
 * - Avoids theme dependencies that could cause rendering issues
 * - Uses regular HTML form elements with validation
 */
const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [resetToken, setResetToken] = useState('');
  const location = useLocation();

  // Extract token from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear field-specific error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Password validation
    if (!formData.password) {
      newErrors.password = t('auth.login.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.reset.tooShort');
    } else if (!STRONG_PASSWORD_RE.test(formData.password)) {
      newErrors.password = t('employee.profile.validation.passwordComplexity');
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.reset.mismatch');
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if token is available
    if (!resetToken) {
      setError('Reset token is missing. Please use the link from the email.');
      return;
    }
    
    // Clear any previous messages
    setSuccessMessage('');
    setError(null);
    
    // Validate the form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await resetPassword(resetToken, formData.password);
      setSuccessMessage(t('auth.reset.success'));
      
    } catch (err) {
      setError(err.code === 'LOGIN_LINK_REQUIRED' ? t('auth.access.passwordMoved') : (err.message || 'Failed to reset password. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reset-password-container">
      <h1 className="reset-password-title">{t('auth.reset.title')}</h1>
      <p>{t('auth.access.privilegedPassword')}</p>
      <p>{t('auth.access.passwordMoved')} <Link to="/login">{t('auth.access.send')}</Link></p>
      
      {!resetToken && (
        <div className="alert-box error">
          Reset token is missing. Please use the link from the email.
        </div>
      )}
      
      {successMessage && (
        <div className="alert-box success" role="status">
          {successMessage}
        </div>
      )}
      
      {error && (
        <div className="alert-box error" role="alert">
          {error}
        </div>
      )}
      
      {!successMessage && <form className="reset-password-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="password" className="form-label">{t('auth.reset.newPassword')}</label>
          <input
            type="password"
            id="password"
            name="password"
            className={`form-input ${errors.password ? 'input-error' : ''}`}
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter new password"
            autoComplete="new-password"
          />
          {errors.password && <div className="error-text">{errors.password}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="confirmPassword" className="form-label">{t('auth.reset.confirmPassword')}</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            className={`form-input ${errors.confirmPassword ? 'input-error' : ''}`}
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm new password"
            autoComplete="new-password"
          />
          {errors.confirmPassword && <div className="error-text">{errors.confirmPassword}</div>}
        </div>
        
        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting || !resetToken}
        >
          {isSubmitting ? t('auth.reset.resetting') : t('auth.reset.reset')}
        </button>
      </form>}
      
      <Link to="/adminlogin" className="back-to-login-link">
        {t('auth.access.adminLogin')}
      </Link>
    </div>
  );
};

export default ResetPassword;
